import { useCallback, useEffect, useState } from "react";
import config from "../config.js";
import {
  resolveVehicleImageUrl,
  getVehicleImageSourceKey,
} from "../components/dashboard/VehicleHoverCard.jsx";
import {
  getReadyVehicleImageUrl,
  getReadyVehicleImageSourceKey,
  setReadyVehicleImageUrl,
  invalidateVehicleImageIfStale,
  isVehicleImageFetchInFlight,
  markVehicleImageFetchInFlight,
} from "../utils/vehicleImageStore.js";

function snapshotReady(vehicles) {
  const next = {};
  (vehicles || []).forEach((vehicle) => {
    const deviceId = vehicle?.device_id;
    if (!deviceId) return;
    const url = getReadyVehicleImageUrl(deviceId);
    if (url) next[deviceId] = url;
  });
  return next;
}

/**
 * Convert a fully-decoded HTMLImageElement into a blob: URL so the hover
 * card never streams a progressive JPEG from the network.
 */
function imageElementToBlobUrl(img) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      if (!canvas.width || !canvas.height) {
        reject(new Error("Invalid image dimensions"));
        return;
      }
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("canvas.toBlob failed (likely CORS)"));
            return;
          }
          resolve(URL.createObjectURL(blob));
        },
        "image/jpeg",
        0.92,
      );
    } catch (err) {
      reject(err);
    }
  });
}

function loadImageElement(networkUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Needed if we later draw to canvas; harmless for plain decode.
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image element failed to load"));
    // Bust HTTP cache when sourceKey changes by callers appending query if needed.
    img.src = networkUrl;
  });
}

/**
 * Prefetch vehicle images into session memory as fully-ready blob URLs.
 * Re-fetches when VehicleImage location/createdAt changes.
 */
export function useVehicleImageCache(vehicles) {
  const [imageUrlsByDeviceId, setImageUrlsByDeviceId] = useState(() =>
    snapshotReady(vehicles),
  );

  const syncFromStore = useCallback(() => {
    setImageUrlsByDeviceId(snapshotReady(vehicles));
  }, [vehicles]);

  useEffect(() => {
    syncFromStore();
  }, [syncFromStore]);

  useEffect(() => {
    if (!vehicles?.length) return;

    let didInvalidate = false;

    vehicles.forEach((vehicle) => {
      const deviceId = vehicle?.device_id;
      if (!deviceId) return;

      const sourceKey = getVehicleImageSourceKey(vehicle);
      if (invalidateVehicleImageIfStale(deviceId, sourceKey)) {
        didInvalidate = true;
      }

      // Image removed from vehicle → nothing to fetch.
      if (!sourceKey) return;

      const existingUrl = getReadyVehicleImageUrl(deviceId);
      const existingKey = getReadyVehicleImageSourceKey(deviceId);
      if (
        existingUrl?.startsWith("blob:") &&
        existingKey === sourceKey
      ) {
        return;
      }
      if (isVehicleImageFetchInFlight(deviceId)) return;

      const networkUrl = resolveVehicleImageUrl(vehicle, null, config.baseURL);
      if (!networkUrl) return;

      // Avoid stale browser HTTP cache for the same path after replace.
      const fetchUrl = networkUrl.includes("?")
        ? `${networkUrl}&v=${encodeURIComponent(sourceKey)}`
        : `${networkUrl}?v=${encodeURIComponent(sourceKey)}`;

      const task = (async () => {
        try {
          const res = await fetch(fetchUrl, { credentials: "include" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          if (!blob || !String(blob.type).startsWith("image/")) {
            throw new Error(`Not an image: ${blob?.type}`);
          }
          await blob.arrayBuffer();
          const objectUrl = URL.createObjectURL(blob);
          setReadyVehicleImageUrl(deviceId, objectUrl, sourceKey);
          syncFromStore();
          return;
        } catch (_) {
          // fall through
        }

        try {
          const img = await loadImageElement(fetchUrl);
          try {
            const objectUrl = await imageElementToBlobUrl(img);
            setReadyVehicleImageUrl(deviceId, objectUrl, sourceKey);
            syncFromStore();
            return;
          } catch (_) {
            setReadyVehicleImageUrl(deviceId, fetchUrl, sourceKey);
            syncFromStore();
          }
        } catch (_) {
          // Leave unset → hover shows bus icon.
        }
      })();

      markVehicleImageFetchInFlight(deviceId, task);
    });

    if (didInvalidate) syncFromStore();
  }, [vehicles, syncFromStore]);

  const getCachedImageUrl = useCallback(
    (deviceId) =>
      imageUrlsByDeviceId[deviceId] ||
      getReadyVehicleImageUrl(deviceId) ||
      null,
    [imageUrlsByDeviceId],
  );

  return { imageUrlsByDeviceId, getCachedImageUrl };
}
